import { useEffect } from "react";
import { bool, node } from "prop-types";
import { observer } from "mobx-react";
import { pdfjs, Document, Page } from "react-pdf";
import { PDF_DOCUMENT_CLASS, SCALE_PRESETS } from "@utils";
import { useContainerScrollHandler, useResizeObserver, useEditorWebsocket, useStores } from "@hooks";
import { useEstimateEditorStore } from "@tools";
import Grid from "@material-ui/core/Grid";
import useStyle from "./ViewBox.style";
import classnames from "classnames";

import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const ViewBox = observer(({
  children,
  showPageImages
}) => {

  const editorStore = useEstimateEditorStore();
  const { stateStore } = useStores();
  const socket = useEditorWebsocket();
  const {
    pdfDocument,
    pdfPagesLoaded,
    headerPages,
    footerPages,
    scale,
    scaleValue
  } = editorStore;

  const classes = useStyle();
  
  const handleContainerScroll = useContainerScrollHandler()
  
  const handleContainerRef = (ref) => {
    editorStore.setPageViewRef(ref);
    stateStore.setAuxiliaryScrollRef(ref);
  }
  
  useEffect(() => {
    stateStore.setUseAuxiliaryScrollRef(true);
    return () => {
      stateStore.setUseAuxiliaryScrollRef(false);
    }
  }, [])
  
  useEffect(() => {
    if(pdfPagesLoaded && scaleValue !== SCALE_PRESETS.CUSTOM)
      handleRescale();
  }, [pdfPagesLoaded])

  const handleScroll = (e) => {
    editorStore.findCurrentPage();
    handleContainerScroll(e);
  };
  
  const onDocumentLoad = async (data) => {
    const initialUsedPdfPages = await editorStore.setupDocument(data);
    if(initialUsedPdfPages) {
      socket?.updateCoverPages?.(pdfDocument.id, initialUsedPdfPages.usedPdfPages, editorStore.proposalTablePosition);
      if(initialUsedPdfPages.scale)
        socket?.updateCoverScale?.(pdfDocument.id, { scale: initialUsedPdfPages.scale})
    }
  }
  
  const handleRescale = () => {
    editorStore.setScale();
  }

  useResizeObserver(handleRescale, editorStore.pageViewRef);
  
  const handleRenderSuccess = (e) => {
    editorStore.handlePageLoaded(e.pageNumber, e.originalWidth, e.originalHeight)
  }
  
  useEffect(() => {
    editorStore.applyPdfScrollPosition();
  }, [editorStore.scale]);
  
  return (
    <Grid
      item container
      className="w-full h-full overflow-hidden"
    >
      <div
        ref={handleContainerRef}
        onScroll={handleScroll}
        className="w-full h-full overflow-auto overflow-anchor"
      >
        <Grid
          item
          container
          // justifyContent="flex-end"
          alignItems="center"
          direction="column"
          ref={editorStore.setContainerRef}
          wrap="nowrap"
          className={classes.container}
        >
          {
            pdfDocument
              ? (
                <Document
                  file={ pdfDocument.file.path }
                  onLoadSuccess={onDocumentLoad}
                  className={classnames(PDF_DOCUMENT_CLASS, classes.document)}
                >
                  {
                    headerPages.map((p) => (
                      <Page
                        key={`page${p}`}
                        pageNumber={p}
                        className={classes.page}
                        scale={scale}
                        onRenderSuccess={handleRenderSuccess}
                      >
                        {
                          showPageImages &&
                          <img
                            className={classes.image}
                            src={editorStore.getPageImageUrl(p)}
                            draggable={false}
                            alt=""
                          />
                        }
                      </Page>
                    ))
                  }
                  {children}
                  {footerPages.map((p) => {
                    return (
                      <Page
                        key={`page${p}`}
                        pageNumber={p}
                        className={classes.page}
                        scale={scale}
                        onRenderSuccess={handleRenderSuccess}
                      >
                        {
                          showPageImages &&
                          <img
                            className={classes.image}
                            src={editorStore.getPageImageUrl(p)}
                            draggable={false}
                            alt=""
                          />
                        }
                      </Page>
                    );
                  })}
                </Document>
              )
              : children
          }
        </Grid>
      </div>
    </Grid>
  );
});

ViewBox.propTypes = {
  showPageImages: bool,
  children: node,
};
